import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    RefreshCw, Search, ChevronDown, Handshake, Download, TrendingUp,
    IndianRupee, CheckCircle2, CircleDot, MapPin, ArrowRight,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import { CHANNEL_PARTNER_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import StateFilterSelect from "../../components/common/StateFilterSelect";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import dayjs from "dayjs";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

// The endpoint reads either format; YYYY-MM-DD is what the date inputs give.
const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "today":      return { from_date: fmt(today), to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")), to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7, "day")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
        default:           return {};
    }
};

const ORDER_OPTIONS = [
    { value: "leads",      label: "Leads Sent" },
    { value: "won",        label: "Won" },
    { value: "conversion", label: "Conversion %" },
    { value: "won_value",  label: "Won Value" },
    { value: "open_value", label: "Open Value" },
];

const num   = (n) => (n ?? 0).toLocaleString("en-IN");
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const Tile = ({ icon: Icon, label, value, tone, hint }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
        <div className="flex items-center gap-1.5 mb-1">
            <Icon className={cn("h-3.5 w-3.5", tone)} />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
        </div>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
        {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
);

/** Which channel partners are actually sending business, and what it turned into.
 *
 *  Every partner on record appears, dormant ones included with zeroes — the
 *  question "who has gone quiet" is half of why this report exists, and a
 *  leaderboard that silently drops partners with no leads cannot answer it.
 */
export default function ChannelPartnerReport() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [datePreset, setDatePreset] = usePersistedState("cp_report_preset", "all");
    const [customFrom, setCustomFrom] = usePersistedState("cp_report_from", "");
    const [customTo,   setCustomTo]   = usePersistedState("cp_report_to", "");
    const [orderBy,    setOrderBy]    = usePersistedState("cp_report_order", "leads");
    const [hideDormant, setHideDormant] = usePersistedState("cp_report_hide_dormant", false);
    const [stateFilter, setStateFilter] = usePersistedState("cp_report_state", "");

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [resp, setResp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const buildParams = useCallback(() => {
        const params = new URLSearchParams(resolveDateRange(datePreset, customFrom, customTo));
        params.set("order_by", orderBy);
        if (search) params.set("search", search);
        if (stateFilter) params.set("state", stateFilter);
        return params;
    }, [datePreset, customFrom, customTo, orderBy, search, stateFilter]);

    const load = useCallback(() => {
        setLoading(true);
        fetch(`${CHANNEL_PARTNER_REPORT_URL}?${buildParams().toString()}`)
            .then(r => {
                if (r?.status === "success") setResp(r);
                else ToastMsg("error", r?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [buildParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const downloadExcel = async () => {
        setExporting(true);
        try {
            const params = buildParams();
            params.append("export_type", "excel");
            const data = await apiClient.get(`${CHANNEL_PARTNER_REPORT_URL}?${params.toString()}`, {
                responseType: "blob",
                timeout: 300000,
            });
            const blob = data instanceof Blob
                ? data
                : new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Channel_Partner_Sourcing_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            ToastMsg("error", "Could not generate the Excel export");
        } finally {
            setExporting(false);
        }
    };

    const totals = resp?.totals;
    const allRows = resp?.data || [];
    const rows = hideDormant ? allRows.filter(r => r.leads > 0) : allRows;

    return (
        <ContainerCard>
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Channel Partner Sourcing</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Which partners send leads, and what those leads turned into.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={downloadExcel} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={exporting || loading}>
                            <Download className={cn("h-3 w-3", exporting && "animate-pulse")} />
                            {exporting ? "Exporting…" : "Excel"}
                        </Button>
                        <Button onClick={load} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* ── filters ── */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative">
                        <select value={datePreset}
                            onChange={e => {
                                setDatePreset(e.target.value);
                                if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                            }}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    {datePreset === "custom" && (
                        <>
                            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                            <span className="text-xs text-gray-400">to</span>
                            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        </>
                    )}

                    <StateFilterSelect value={stateFilter} onChange={setStateFilter} />

                    <div className="relative">
                        <select value={orderBy} onChange={e => setOrderBy(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                            {ORDER_OPTIONS.map(o => <option key={o.value} value={o.value}>Rank by {o.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <button
                        onClick={() => setHideDormant(!hideDormant)}
                        className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                            hideDormant
                                ? "bg-indigo-600 text-white border-indigo-700"
                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700")}
                        title="Hide partners who have not sent a lead in the selected period"
                    >
                        Sending only
                    </button>

                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <input value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && setSearch(searchInput)}
                            placeholder="Partner name…"
                            className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 w-44" />
                    </div>
                </div>

                {/* ── totals ── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                    <Tile icon={Handshake}    label="Partners"    tone="text-purple-600" value={num(totals?.partners)}
                          hint={`${num(totals?.active_partners)} sending`} />
                    <Tile icon={CircleDot}    label="Leads Sent"  tone="text-blue-600"   value={num(totals?.leads)} />
                    <Tile icon={CheckCircle2} label="Won"         tone="text-green-600"  value={num(totals?.won)} />
                    <Tile icon={TrendingUp}   label="Conversion"  tone="text-indigo-600" value={`${totals?.conversion_rate_pct ?? 0}%`}
                          hint="of decided leads" />
                    <Tile icon={IndianRupee}  label="Won Value"   tone="text-green-600"  value={money(totals?.won_value)} />
                    <Tile icon={IndianRupee}  label="Open Value"  tone="text-blue-600"   value={money(totals?.open_value)} />
                </div>
            </div>

            {/* ── leaderboard ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading report…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Channel Partner</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Partner Stage</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Leads</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Won</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Open</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Lost</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Conv.</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Won Value</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Open Value</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Last Lead</th>
                                    <th className="px-3 py-2.5 w-8" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={12} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No channel partners match these filters
                                        </td>
                                    </tr>
                                ) : rows.map((r, idx) => (
                                    <tr key={r.id}
                                        onClick={() => navigate(`/customers/customerdetails?id=${r.id}`)}
                                        className={cn("hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors",
                                            r.leads === 0 && "opacity-60")}>
                                        <td className="px-3 py-2.5 text-gray-400">{idx + 1}</td>
                                        <td className="px-3 py-2.5 max-w-[220px]">
                                            <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{r.customer_name || "—"}</p>
                                            {r.city && (
                                                <p className="flex items-center gap-0.5 text-[10px] text-gray-400 mt-0.5">
                                                    <MapPin className="h-2.5 w-2.5" />{r.city}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {r.lead_stage ? (
                                                <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(r.lead_stage))}>
                                                    {r.lead_stage}
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-semibold text-gray-800 dark:text-gray-200">{num(r.leads)}</td>
                                        <td className="px-3 py-2.5 text-right text-green-600 dark:text-green-400">{num(r.won)}</td>
                                        <td className="px-3 py-2.5 text-right text-blue-600 dark:text-blue-400">{num(r.open)}</td>
                                        <td className="px-3 py-2.5 text-right text-red-500">{num(r.lost)}</td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">
                                            {r.won + r.lost > 0 ? `${r.conversion_rate_pct}%` : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {r.won_value ? money(r.won_value) : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {r.open_value ? money(r.open_value) : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {r.last_lead_date ? dayjs(r.last_lead_date).format("DD MMM YYYY") : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-300"><ArrowRight className="h-3 w-3" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {rows.length} of {allRows.length} partners
                            {hideDormant && allRows.length !== rows.length && " — dormant partners hidden"}
                        </span>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
}
